import React, { useEffect, useState } from "react";
import {
  CircularProgress,
  Box,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  Tooltip,
  Paper,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import {
  get_application_api,
  update_application_status_api,
  add_application_note_api,
} from "../../DAL/Careers/Careers";

const STATUS_OPTIONS = ["new", "reviewing", "interviewing", "offered", "rejected", "hired"];

const getStatusChipSx = (status) => {
  switch (status) {
    case "new": return { backgroundColor: "rgba(37,99,235,0.1)", color: "#2563eb" };
    case "reviewing":
    case "interviewing": return { backgroundColor: "rgba(217,119,6,0.1)", color: "#d97706" };
    case "offered":
    case "hired": return { backgroundColor: "rgba(22,163,74,0.1)", color: "#16a34a" };
    case "rejected": return { backgroundColor: "rgba(192,30,46,0.1)", color: "#c01e2e" };
    default: return { backgroundColor: "rgba(26,26,26,0.06)", color: "#6b6b6b" };
  }
};

const InfoItem = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <Typography sx={{ color: "#aaa", fontSize: "11px", mb: 0.5, fontFamily: "'Rubik', sans-serif", textTransform: "uppercase", letterSpacing: "0.3px" }}>{label}</Typography>
    <Typography sx={{ color: "#1a1a1a", fontSize: "14px", fontFamily: "'Rubik', sans-serif" }}>{value || "—"}</Typography>
  </Box>
);

export default function ApplicationDetail() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { application_id } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [application, setApplication] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    getApplication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application_id]);

  const getApplication = async () => {
    try {
      const result = await get_application_api(application_id);
      if (result.code === 200) {
        setApplication(result.data);
      } else {
        enqueueSnackbar(result.message || "Failed to load application", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("Error fetching application", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (e) => {
    const status = e.target.value;
    const prev = application.status;
    setApplication((p) => ({ ...p, status }));
    const result = await update_application_status_api(application_id, status);
    if (result.code === 200) {
      enqueueSnackbar("Status updated", { variant: "success" });
    } else {
      setApplication((p) => ({ ...p, status: prev }));
      enqueueSnackbar(result.message || "Failed to update status", { variant: "error" });
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    const result = await add_application_note_api(application_id, noteText.trim());
    if (result.code === 200) {
      setApplication(result.data);
      setNoteText("");
      enqueueSnackbar("Note added", { variant: "success" });
    } else {
      enqueueSnackbar(result.message || "Failed to add note", { variant: "error" });
    }
    setSavingNote(false);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress sx={{ color: "#c01e2e" }} />
      </Box>
    );
  }

  if (!application) {
    return (
      <Box sx={{ p: 3, textAlign: "center", color: "#aaa", fontFamily: "'Rubik', sans-serif" }}>
        Application not found.
      </Box>
    );
  }

  const initials = (application.fullName || "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, minHeight: "calc(100vh - 48px)" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
        <Tooltip title="Back to Applications">
          <IconButton onClick={() => navigate("/careers/applications")} sx={{ color: "#6b6b6b" }}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Avatar sx={{ width: 44, height: 44, backgroundColor: "#c01e2e", fontSize: "15px" }}>{initials}</Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "#1a1a1a", fontFamily: "'Rubik', sans-serif" }}>
            {application.fullName}
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "#aaa", fontFamily: "'Rubik', sans-serif" }}>
            Applied for {application.jobTitleSnapshot} · {application.createdAt ? moment(application.createdAt).format("DD MMM YYYY") : ""}
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            value={application.status}
            onChange={handleStatusChange}
            sx={{
              backgroundColor: "#ffffff",
              borderRadius: "9px",
              fontSize: "13px",
              fontFamily: "'Rubik', sans-serif",
              textTransform: "capitalize",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(26,26,26,0.16)" },
            }}
          >
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s} sx={{ textTransform: "capitalize", fontFamily: "'Rubik', sans-serif" }}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Chip
          label={application.status}
          size="small"
          sx={{ ...getStatusChipSx(application.status), borderRadius: "6px", fontSize: "11px", fontWeight: 500, fontFamily: "'Rubik', sans-serif", textTransform: "capitalize" }}
        />
      </Box>

      <Paper sx={{ backgroundColor: "#ffffff", border: "1px solid rgba(26,26,26,0.09)", borderRadius: "14px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            borderBottom: "1px solid rgba(26,26,26,0.09)",
            px: 2,
            "& .MuiTab-root": { textTransform: "none", fontFamily: "'Rubik', sans-serif", fontSize: "13px", fontWeight: 500, color: "#6b6b6b" },
            "& .Mui-selected": { color: "#c01e2e !important" },
            "& .MuiTabs-indicator": { backgroundColor: "#c01e2e" },
          }}
        >
          <Tab label="Overview" value="overview" />
          <Tab label="Experience" value="experience" />
          <Tab label="Cover Letter" value="cover-letter" />
          <Tab label={`Notes (${application.notes?.length || 0})`} value="notes" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === "overview" && (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <InfoItem label="Email" value={application.email} />
              <InfoItem label="Phone" value={application.phone} />
              <InfoItem label="Location" value={application.location} />
              <InfoItem label="LinkedIn" value={application.linkedIn} />
              <InfoItem label="Current Role" value={application.currentRole} />
              <InfoItem label="Years of Experience" value={application.yearsExperience} />
              <InfoItem label="Right to Work (UK)" value={application.rightToWorkUK ? "Yes" : "No"} />
              <InfoItem label="Availability" value={application.availability} />
              <InfoItem label="Salary Expectation" value={application.salaryExpectation} />
              <InfoItem label="Source" value={application.source} />
            </Box>
          )}

          {activeTab === "experience" && (
            <Box>
              <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1a1a1a", fontFamily: "'Rubik', sans-serif", mb: 1.5, textTransform: "uppercase", letterSpacing: "0.3px" }}>
                Work History
              </Typography>
              {(application.workHistory || []).length === 0 && (
                <Typography sx={{ color: "#aaa", fontSize: "13px", fontFamily: "'Rubik', sans-serif", mb: 2 }}>None provided.</Typography>
              )}
              {(application.workHistory || []).map((w, i) => (
                <Paper key={i} sx={{ p: 2, mb: 1.5, border: "1px solid rgba(26,26,26,0.08)", borderRadius: "10px", boxShadow: "none" }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1a1a1a", fontFamily: "'Rubik', sans-serif" }}>{w.title} — {w.company}</Typography>
                  <Typography sx={{ fontSize: "12px", color: "#aaa", fontFamily: "'Rubik', sans-serif", mb: 0.5 }}>{w.startDate} – {w.endDate}</Typography>
                  <Typography sx={{ fontSize: "13px", color: "#2d2d2d", fontFamily: "'Rubik', sans-serif" }}>{w.description}</Typography>
                </Paper>
              ))}

              <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1a1a1a", fontFamily: "'Rubik', sans-serif", mb: 1.5, mt: 3, textTransform: "uppercase", letterSpacing: "0.3px" }}>
                Education
              </Typography>
              {(application.education || []).length === 0 && (
                <Typography sx={{ color: "#aaa", fontSize: "13px", fontFamily: "'Rubik', sans-serif", mb: 2 }}>None provided.</Typography>
              )}
              {(application.education || []).map((ed, i) => (
                <Paper key={i} sx={{ p: 2, mb: 1.5, border: "1px solid rgba(26,26,26,0.08)", borderRadius: "10px", boxShadow: "none" }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1a1a1a", fontFamily: "'Rubik', sans-serif" }}>{ed.degree} {ed.field ? `— ${ed.field}` : ""}</Typography>
                  <Typography sx={{ fontSize: "12px", color: "#aaa", fontFamily: "'Rubik', sans-serif" }}>{ed.institution} · {ed.startYear} – {ed.endYear}</Typography>
                </Paper>
              ))}

              <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1a1a1a", fontFamily: "'Rubik', sans-serif", mb: 1.5, mt: 3, textTransform: "uppercase", letterSpacing: "0.3px" }}>
                Skills
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {(application.skills || []).length === 0 && (
                  <Typography sx={{ color: "#aaa", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>None provided.</Typography>
                )}
                {(application.skills || []).map((s, i) => (
                  <Chip key={i} label={s} size="small" sx={{ backgroundColor: "rgba(192,30,46,0.06)", color: "#c01e2e", fontFamily: "'Rubik', sans-serif", fontSize: "12px" }} />
                ))}
              </Box>
            </Box>
          )}

          {activeTab === "cover-letter" && (
            <Typography sx={{ fontSize: "14px", color: "#2d2d2d", fontFamily: "'Rubik', sans-serif", whiteSpace: "pre-line", lineHeight: 1.7 }}>
              {application.coverLetter || "No cover letter provided."}
            </Typography>
          )}

          {activeTab === "notes" && (
            <Box>
              {(application.notes || []).length === 0 && (
                <Typography sx={{ color: "#aaa", fontSize: "13px", fontFamily: "'Rubik', sans-serif", mb: 2 }}>No notes yet.</Typography>
              )}
              {(application.notes || []).map((n) => (
                <Paper key={n._id} sx={{ p: 2, mb: 1.5, border: "1px solid rgba(26,26,26,0.08)", borderRadius: "10px", boxShadow: "none" }}>
                  <Typography sx={{ fontSize: "13px", color: "#2d2d2d", fontFamily: "'Rubik', sans-serif" }}>{n.text}</Typography>
                  <Typography sx={{ fontSize: "11px", color: "#aaa", fontFamily: "'Rubik', sans-serif", mt: 0.5 }}>
                    {n.authorName} · {n.createdAt ? moment(n.createdAt).fromNow() : ""}
                  </Typography>
                </Paper>
              ))}
              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Add a note…"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff", borderRadius: "9px", fontFamily: "'Rubik', sans-serif" } }}
                />
                <Button
                  variant="contained"
                  disabled={savingNote || !noteText.trim()}
                  onClick={handleAddNote}
                  sx={{ backgroundColor: "#c01e2e", color: "#fff", textTransform: "none", fontFamily: "'Rubik', sans-serif", borderRadius: "8px", boxShadow: "none", "&:hover": { backgroundColor: "#a81828", boxShadow: "none" } }}
                >
                  Add
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}